import { useQuery } from "@tanstack/react-query";

import { api } from "../api/client";
import { DeviceStatusPill } from "./DeviceStatusPill";
import { StatusSparkline } from "./StatusSparkline";

interface DeviceDetailViewProps {
  tenantSlug: string;
  tenantDisplayName: string;
  deviceId: number;
  onBack: () => void;
}

export function DeviceDetailView({
  tenantSlug,
  tenantDisplayName,
  deviceId,
  onBack,
}: DeviceDetailViewProps) {
  const detailQuery = useQuery({
    queryKey: ["device-detail", tenantSlug, deviceId],
    queryFn: () => api.deviceDetail(tenantSlug, deviceId, 24),
    refetchInterval: 30_000,
  });

  return (
    <div>
      <button onClick={onBack} style={backButtonStyle}>
        ← {tenantDisplayName}
      </button>

      {detailQuery.isLoading && (
        <p style={{ color: "var(--text-dim)", marginTop: 16 }}>Loading device…</p>
      )}
      {detailQuery.error && (
        <p style={{ color: "var(--crit)", marginTop: 16 }}>
          Failed to load device: {String(detailQuery.error)}
        </p>
      )}

      {detailQuery.data && (
        <article>
          <header style={{ marginTop: 16, marginBottom: 24 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
              <h2 style={{ margin: 0, fontSize: "1.4rem" }}>
                {detailQuery.data.intent_label ||
                  detailQuery.data.reality_label ||
                  detailQuery.data.external_id}
              </h2>
              <DeviceStatusPill status={detailQuery.data.status} />
              <span style={{ color: "var(--text-dim)", fontSize: "0.85rem" }}>
                {detailQuery.data.device_class}
              </span>
            </div>
            <p style={{ color: "var(--text-dim)", margin: "4px 0 0", fontSize: "0.85rem" }}>
              <code>{detailQuery.data.external_id}</code> · last seen{" "}
              {detailQuery.data.last_observation_at
                ? new Date(detailQuery.data.last_observation_at).toLocaleString()
                : "never"}
            </p>
          </header>

          <Section title="24 h status timeline">
            <StatusSparkline
              observations={detailQuery.data.observations}
              windowStart={detailQuery.data.window_started_at}
              windowEnd={detailQuery.data.window_ended_at}
            />
          </Section>

          <Section title="Reality (from Home Assistant)">
            <KeyValueGrid
              rows={[
                ["Status", detailQuery.data.status],
                ["Reality label", detailQuery.data.reality_label || "—"],
                [
                  "Last state change",
                  detailQuery.data.last_state_change
                    ? new Date(detailQuery.data.last_state_change).toLocaleString()
                    : "—",
                ],
                detailQuery.data.last_error
                  ? ["Last error", detailQuery.data.last_error]
                  : null,
              ]}
            />
          </Section>

          <Section title="Intent (from inventory)">
            <KeyValueGrid
              rows={[
                ["Intent label", detailQuery.data.intent_label || "—"],
                ["Device class", detailQuery.data.device_class],
                ["External ID", detailQuery.data.external_id],
              ]}
            />
          </Section>

          <Section title="Raw observation payload">
            <pre
              style={{
                background: "var(--surface-2)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                padding: 12,
                fontSize: "0.78rem",
                overflowX: "auto",
                margin: 0,
              }}
            >
              {JSON.stringify(detailQuery.data.raw, null, 2)}
            </pre>
          </Section>
        </article>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      style={{
        marginBottom: 24,
        padding: "16px 18px",
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 8,
      }}
    >
      <h3
        style={{
          margin: "0 0 12px",
          fontSize: "0.78rem",
          textTransform: "uppercase",
          color: "var(--text-dim)",
          letterSpacing: "0.05em",
        }}
      >
        {title}
      </h3>
      {children}
    </section>
  );
}

function KeyValueGrid({ rows }: { rows: (readonly [string, string] | null)[] }) {
  const visible = rows.filter((r): r is readonly [string, string] => r !== null);
  return (
    <dl
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(140px, max-content) 1fr",
        rowGap: 6,
        columnGap: 16,
        margin: 0,
        fontSize: "0.85rem",
      }}
    >
      {visible.map(([k, v]) => (
        <div key={k} style={{ display: "contents" }}>
          <dt style={{ color: "var(--text-dim)" }}>{k}</dt>
          <dd style={{ margin: 0 }}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

const backButtonStyle: React.CSSProperties = {
  background: "transparent",
  color: "var(--accent)",
  border: "none",
  padding: "4px 0",
  fontSize: "0.85rem",
  cursor: "pointer",
};
